import {
	ReadonlyURLSearchParams,
	useParams,
	useSearchParams,
} from 'next/navigation';
import { useCallback, useMemo } from 'react';

import { routes } from '~constants/routes';
import annalsGrid from '~features/annals/hooks/grid';
import {
	CreatedBy,
	type TGridFiltersValues,
} from '~features/gridFilters/model/types';
import { parseFilters } from '~features/gridFilters/utils/parseFilters';
import { saveToSessionStorage, ssKeys } from '~features/storage';
import { usePathname, useRouter } from '~i18n/navigation';
import { isSafeApp } from '~shared/utils/host';
import { updateQueryParamsWithPathname } from '~shared/utils/searchParams';
import { getQueryObjectFromSearchParams } from '~utils/getQueryObjectFromSearchParams';

import {
	defaultGridFilters,
	FILTER_MAX_AGE,
	FILTER_MIN_AGE,
} from '../model/config';
import { GridFiltersFormFields as fields } from '../model/names';

const stringifyValues = (values: Record<string, { toString(): string }>) =>
	Object.fromEntries(
		Object.entries(values).map(([key, value]) => [key, value.toString()])
	);

const emptySearchParams = new ReadonlyURLSearchParams();

export function useGridFilters() {
	const searchParams = useSearchParams();
	const pathname = usePathname();
	const router = useRouter();
	const { slug: slugArray = [] } = useParams();
	const sendFiltersSubmitted = annalsGrid.useGridFiltersSubmitted();
	const slug = slugArray?.[1];
	const isIndexPage = slugArray.length === 0;

	// TODO: Drop unsupported filters
	const filters = useMemo(() => {
		const hasSearchParams = searchParams.toString() !== '';
		if (hasSearchParams) {
			return parseFilters(
				getQueryObjectFromSearchParams(searchParams)
			) as TGridFiltersValues;
		}

		const canUseDefaultFilters = !isSafeApp && isIndexPage;
		return (
			canUseDefaultFilters ? defaultGridFilters : {}
		) as TGridFiltersValues;
	}, [isIndexPage, searchParams]);

	const sendFiltersDataToAnnals = useCallback(
		(
			filters: TGridFiltersValues,
			isManual: boolean,
			slug: string | undefined
		) => {
			const data = {
				text_filtered: filters[fields.keyword] || 'NULL',
				createdBy: filters[fields.createdBy] || undefined,
				gender: filters[fields.gender] || undefined,
				age: `${(filters[fields.fromAge] || FILTER_MIN_AGE).toString()}-${(
					filters[fields.toAge] || FILTER_MAX_AGE
				).toString()}`,
				safe: isSafeApp ? ('SFW' as const) : ('NSFW' as const),
				filters_manual: isManual ? (1 as const) : (0 as const),
				desires: slug,
				// TODO: https://issues.sdventures.com/browse/IFRIEND-10290
				// Rethink annals filters - not view, submit - and send filtering from backend
				// sorting_bot_grid: [], // grid.map((item) => item.clientId.toString()),
			};

			sendFiltersSubmitted(data);
		},
		[sendFiltersSubmitted]
	);

	const applyFilters = (newFilters: TGridFiltersValues, clear?: boolean) => {
		const nonEmptyFilters = Object.fromEntries(
			Object.entries(newFilters).filter(
				([, value]) => value !== null && value !== undefined && value !== ''
			)
		);
		const hasFilters = Object.keys(nonEmptyFilters).length > 0;
		void router.push(
			updateQueryParamsWithPathname(
				nonEmptyFilters,
				hasFilters && !clear ? pathname : routes.explore.models,
				emptySearchParams
			)
		);
		saveToSessionStorage(
			ssKeys.GRID_FILTERS,
			hasFilters ? nonEmptyFilters : null
		);
		saveToSessionStorage(ssKeys.GRID_FILTERS_MANUAL_UPDATE, true);

		sendFiltersDataToAnnals(
			nonEmptyFilters,
			true,
			hasFilters ? slugArray?.[1] : undefined
		);
	};

	const updateFilters = (newFilters: TGridFiltersValues) =>
		applyFilters(
			stringifyValues({
				...filters,
				...newFilters,
			})
		);

	const clearFilters = () =>
		applyFilters(
			{
				createdBy: CreatedBy.all,
			},
			true
		);

	const resetManualUpdate = () =>
		saveToSessionStorage(ssKeys.GRID_FILTERS_MANUAL_UPDATE, false);

	return {
		filters,
		count: Object.keys(filters).length,
		updateFilters,
		applyFilters,
		clearFilters,
		sendFiltersDataToAnnals,
		resetManualUpdate,
		slug,
	};
}
